export type MobileOs = 'ios' | 'android';
export type MobileBrowser = 'safari' | 'chrome' | 'other';

export type MobilePlatform = {
	os: MobileOs;
	browser: MobileBrowser;
};

type DetectMobilePlatformInput = {
	userAgent: string;
	/** `navigator.maxTouchPoints`, which is how an iPad presenting a Mac user agent still reads as touch. */
	maxTouchPoints?: number;
};

const IOS_PATTERN = /iPhone|iPod/i;
const IPAD_PATTERN = /iPad/i;
const ANDROID_PATTERN = /Android/i;
const CHROME_PATTERN = /CriOS|Chrome|Chromium|EdgiOS|EdgA/i;
const SAFARI_PATTERN = /Safari/i;
const FIREFOX_PATTERN = /FxiOS|Firefox/i;

function detectOs({ userAgent, maxTouchPoints }: DetectMobilePlatformInput): MobileOs | null {
	if (IOS_PATTERN.test(userAgent) || IPAD_PATTERN.test(userAgent)) return 'ios';
	if (/Macintosh/i.test(userAgent) && (maxTouchPoints ?? 0) > 1) return 'ios';
	if (ANDROID_PATTERN.test(userAgent)) return 'android';
	return null;
}

function detectBrowser(userAgent: string): MobileBrowser {
	if (FIREFOX_PATTERN.test(userAgent)) return 'other';
	if (CHROME_PATTERN.test(userAgent)) return 'chrome';
	if (SAFARI_PATTERN.test(userAgent)) return 'safari';
	return 'other';
}

/** Which phone OS and browser family a user agent belongs to; `null` for anything that is not a phone or tablet. */
export function detectMobilePlatform(input: DetectMobilePlatformInput): MobilePlatform | null {
	const os = detectOs(input);
	if (!os) return null;
	return { os, browser: detectBrowser(input.userAgent) };
}

const DEV_OVERRIDE_PARAM = 'mobile';

function isMobileOs(value: string | null): value is MobileOs {
	return value === 'ios' || value === 'android';
}

/** Local development only: `?mobile=ios|android` previews the phone layout from a desktop browser. */
function readDevOverride(): MobilePlatform | null {
	if (!import.meta.env.DEV || typeof window === 'undefined') return null;
	const os = new URLSearchParams(window.location.search).get(DEV_OVERRIDE_PARAM);
	if (!isMobileOs(os)) return null;
	return { os, browser: os === 'ios' ? 'safari' : 'chrome' };
}

export function currentMobilePlatform(): MobilePlatform | null {
	if (typeof navigator === 'undefined') return null;
	return (
		readDevOverride() ??
		detectMobilePlatform({ userAgent: navigator.userAgent, maxTouchPoints: navigator.maxTouchPoints })
	);
}
